import { useEffect, useRef, useState } from 'react'
import { formatBalance } from '@/lib/format-number'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import type { PaymentBalance } from '@/types/payment'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

interface BalanceListItemProps {
	balance: PaymentBalance
	onDeposit: (currencyCode: string) => void
	onSetDefaultBalance: (currencyCode: string) => void
	onDisableBalance: (currencyCode: string) => void
	isSettingDefault: boolean
	isDisabling: boolean
}

export function BalanceListItem({ balance, onDeposit, onSetDefaultBalance, onDisableBalance, isSettingDefault, isDisabling }: BalanceListItemProps) {
	const balanceValue = formatBalance(balance.balance)
	const currencyName = balance.currency?.currency_name ?? balance.currency_code
	const displayName = `${balance.currency_code} ( ${currencyName} )`
	const symbolUrl = balance.currency?.symbol_url
	const nameRef = useRef<HTMLParagraphElement>(null)
	const [isNameTruncated, setIsNameTruncated] = useState(false)

	useEffect(() => {
		const element = nameRef.current
		if (!element) return

		const updateTruncation = () => {
			setIsNameTruncated(element.scrollWidth > element.clientWidth)
		}

		updateTruncation()

		const observer = new ResizeObserver(updateTruncation)
		observer.observe(element)
		return () => observer.disconnect()
	}, [displayName])

	const handleSetDefaultBalanceClick = () => {
		onSetDefaultBalance(balance.currency_code)
	}

	const handleDisableBalanceClick = () => {
		onDisableBalance(balance.currency_code)
	}

	return (
		<div className='flex flex-col gap-4 rounded-xl border border-border bg-secondary p-4 lg:flex-row lg:items-center lg:justify-between'>
			<div className='flex min-w-0 items-center gap-3'>
				<CurrencyIcon code={balance.currency_code} symbolUrl={symbolUrl} size='lg' />
				<div className='min-w-0'>
					<Tooltip open={isNameTruncated ? undefined : false}>
						<TooltipTrigger asChild>
							<p ref={nameRef} className='truncate text-base font-semibold tracking-[-0.24px] text-foreground'>
								{displayName}
							</p>
						</TooltipTrigger>
						<TooltipContent>{displayName}</TooltipContent>
					</Tooltip>
					<p className='mt-1 text-sm font-medium tracking-[-0.21px] text-muted-foreground'>
						{balanceValue} {balance.currency_code}
					</p>
				</div>
			</div>

			<div className='flex shrink-0 flex-nowrap items-center gap-2 overflow-x-auto'>
				{!balance.is_default && (
					<Button variant='secondary' onClick={handleSetDefaultBalanceClick} disabled={isSettingDefault}>
						Set Default
					</Button>
				)}
				<Button variant='primary' onClick={() => onDeposit(balance.currency_code)}>
					Deposit
				</Button>
				<Button variant='secondary'>Withdraw</Button>
				<Button variant='destructive' onClick={handleDisableBalanceClick} disabled={isDisabling}>
					Disable
				</Button>
			</div>
		</div>
	)
}
